import React, { forwardRef, useCallback, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ReactSelect, { createFilter, components as reactSelectComponents } from 'react-select'

import { ReactComponent as ReactSearchSvg } from '../../../assets/icon-search.svg'

// Below this many options the list fits on screen and a search box is just noise.
const SEARCH_THRESHOLD = 8

// react-select matches on label *and* value by default, and most values here are ids, so
// a search for "19" turns up classes whose GUID happens to contain it.
const filterOnLabel = createFilter({ stringify: (option) => option.label })

const MenuWithSearch = ({ children, ...props }) => {
  const { t } = useTranslation()
  const {
    inputValue,
    searchRef,
    onSearchChange,
    onSearchKeyDown,
    onSearchBlur,
  } = props.selectProps

  return (
    <reactSelectComponents.Menu
      {...props}
      innerProps={{
        ...props.innerProps,
        // Keeps the field focused while the list is clicked or scrolled: every mousedown
        // in here would otherwise blur it and close the menu before the click landed.
        onMouseDown: (event) => {
          if (event.target !== searchRef.current) {
            event.preventDefault()
          }
        },
      }}
    >
      <div className="input-select__search">
        <ReactSearchSvg className="input-select__search-icon" />
        <input
          ref={searchRef}
          className="input-select__search-input"
          type="text"
          autoFocus
          autoComplete="off"
          value={inputValue}
          placeholder={`${t(`studentPage.search`)} ...`}
          onChange={onSearchChange}
          onKeyDown={onSearchKeyDown}
          onBlur={onSearchBlur}
        />
      </div>
      {children}
    </reactSelectComponents.Menu>
  )
}

/**
 * Drop-in replacement for react-select that puts a real search field inside the menu.
 *
 * react-select filters as you type out of the box, but only into the control itself and
 * nothing on screen says so. The field here drives the same filter, on the lists long
 * enough to need it and not on multi-selects, which already show a text input.
 *
 * The menu has to be controlled: react-select closes it whenever its own input loses
 * focus, and focusing the search field is exactly that.
 */
const SearchableSelect = forwardRef(({ options, components, isMulti, onChange, ...props }, ref) => {
  const withSearch = !isMulti
    && props.isSearchable !== false
    && (options?.length || 0) >= SEARCH_THRESHOLD

  const [menuIsOpen, setMenuIsOpen] = useState(false)
  const [inputValue, setInputValue] = useState('')
  const searchRef = useRef(null)
  const reclaimFocus = useRef(false)

  const closeMenu = useCallback(() => {
    reclaimFocus.current = false
    setMenuIsOpen(false)
    setInputValue('')
  }, [])

  const mergedComponents = useMemo(() => {
    return withSearch
      ? { ...components, Menu: MenuWithSearch }
      : components
  }, [withSearch, components])

  if (!withSearch) {
    return (
      <ReactSelect
        ref={ref}
        options={options}
        components={components}
        isMulti={isMulti}
        onChange={onChange}
        {...props}
      />
    )
  }

  return (
    <ReactSelect
      {...props}
      ref={ref}
      options={options}
      components={mergedComponents}
      isMulti={isMulti}
      isSearchable={false}
      filterOption={props.filterOption || filterOnLabel}
      menuIsOpen={menuIsOpen}
      inputValue={inputValue}
      onMenuOpen={() => {
        reclaimFocus.current = true
        setMenuIsOpen(true)
      }}
      // Deliberately ignored: react-select asks to close as soon as its own input blurs,
      // which is the moment the search field takes focus. The field's own blur, Escape
      // and picking an option are what close the menu instead.
      onMenuClose={() => {}}
      onChange={(value, actionMeta) => {
        closeMenu()
        if (onChange) {
          onChange(value, actionMeta)
        }
      }}
      searchRef={searchRef}
      onSearchChange={(event) => setInputValue(event.target.value)}
      onSearchKeyDown={(event) => {
        if (event.key === 'Escape') {
          closeMenu()
        }
      }}
      onSearchBlur={() => {
        // react-select pulls focus back to its own input on the render that opens the
        // menu ("ensure focus is on the Input when the menu opens"). Take it back once;
        // every later blur is the user leaving the field for real.
        if (reclaimFocus.current) {
          reclaimFocus.current = false
          searchRef.current.focus()
          return
        }

        closeMenu()
      }}
    />
  )
})

SearchableSelect.displayName = 'SearchableSelect'

export default SearchableSelect
